import { clipHttpClient } from "./createClipHttpClient";
import type { ClipHttpContext } from "./types";
import type { HttpMethod } from "@kalodata/http";
import type { paths } from "@apis/generated/openapi";
import { routeMeta } from "@apis/generated/routeMeta";
import type { Observable } from "rxjs";

type RouteMetaMap = typeof routeMeta;
export type RouteKey = {
  [K in keyof RouteMetaMap]: RouteMetaMap[K]["method"] extends HttpMethod ? K : never;
}[keyof RouteMetaMap];
type FileLike = File | Blob;
type SupportedMethod = HttpMethod;
type LowerMethod = Lowercase<SupportedMethod>;

type ParseRoute<K extends RouteKey> = K extends `${infer M}/${infer Rest}`
  ? { method: Lowercase<M> & LowerMethod; path: `/${Rest}` & keyof paths }
  : never;

type OperationOf<K extends RouteKey> = paths[ParseRoute<K>["path"]][ParseRoute<K>["method"]];

type RequestContentOf<K extends RouteKey> =
  OperationOf<K> extends { requestBody?: infer RB } ? (RB extends { content: infer C } ? C : never) : never;

type ResponseContentOf<K extends RouteKey> = SuccessResponseOf<K> extends { content: infer C } ? C : never;

type SchemaOf<T> = T extends { schema: infer S } ? S : T;

type SuccessStatusKey<T> = Extract<
  keyof T,
  `${2}${number}` | 200 | 201 | 202 | 203 | 204 | 205 | 206 | 207 | 208 | 226
>;

type SuccessResponseOf<K extends RouteKey> =
  OperationOf<K> extends { responses: infer R }
    ? SuccessStatusKey<R> extends never
      ? never
      : R[SuccessStatusKey<R>]
    : never;

type ContentSchema<C> =
  C extends Record<string, unknown>
    ? "application/json" extends keyof C
      ? SchemaOf<C["application/json"]>
      : "multipart/form-data" extends keyof C
        ? SchemaOf<C["multipart/form-data"]>
        : "*/*" extends keyof C
          ? SchemaOf<C["*/*"]>
          : SchemaOf<C[keyof C]>
    : never;

type BinaryFieldPath<K extends RouteKey> = RouteMetaMap[K]["binaryFields"][number];
type TopLevelBinaryField<K extends RouteKey> =
  BinaryFieldPath<K> extends `${infer T}.${string}` ? T : BinaryFieldPath<K>;

type BodyRawOf<K extends RouteKey> = ContentSchema<RequestContentOf<K>>;

type BinaryBodyOf<K extends RouteKey> =
  BodyRawOf<K> extends Record<string, unknown>
    ? {
        [P in keyof BodyRawOf<K>]: P extends TopLevelBinaryField<K>
          ? BodyRawOf<K>[P] extends Array<unknown>
            ? FileLike[]
            : FileLike
          : BodyRawOf<K>[P];
      }
    : BodyRawOf<K>;

export type QueryOf<K extends RouteKey> =
  OperationOf<K> extends { parameters?: infer P } ? (P extends { query?: infer Q } ? Q : never) : never;

export type PathOf<K extends RouteKey> =
  OperationOf<K> extends { parameters?: infer P } ? (P extends { path?: infer X } ? X : never) : never;

export type BodyOf<K extends RouteKey> = RouteMetaMap[K]["bodyKind"] extends "binary-json" | "multipart"
  ? BinaryBodyOf<K>
  : BodyRawOf<K>;

export type EnvelopeOf<K extends RouteKey> = ContentSchema<ResponseContentOf<K>>;
export type ResponseOf<K extends RouteKey> = EnvelopeOf<K> extends { data?: infer D } ? D : EnvelopeOf<K>;
export type RouteMetaOf<K extends RouteKey> = RouteMetaMap[K];

type HasBody<K extends RouteKey> = RouteMetaMap[K]["bodyKind"] extends "none" ? false : true;
type HasQuery<K extends RouteKey> = RouteMetaMap[K]["hasQuery"];
type HasPath<K extends RouteKey> = RouteMetaMap[K]["pathParams"]["length"] extends 0 ? false : true;

type IsAllOptional<T> = [T] extends [never] ? true : {} extends T ? true : false;

type RequestExtras = {
  timeoutMs?: number;
  context?: ClipHttpContext;
};

type CompositeArgs<K extends RouteKey> = (HasQuery<K> extends true ? { query: QueryOf<K> } : {}) &
  (HasBody<K> extends true ? { body: BodyOf<K> } : {}) &
  (HasPath<K> extends true ? { path: PathOf<K> } : {}) &
  RequestExtras;

type CallOptions = {
  /** 用于取消请求的 AbortSignal */
  signal?: AbortSignal;
};

type SingleArgFn<T, R> =
  IsAllOptional<T> extends true
    ? (input?: T, options?: CallOptions) => Promise<R>
    : (input: T, options?: CallOptions) => Promise<R>;
type SingleArgFn$<T, R> =
  IsAllOptional<T> extends true
    ? (input?: T, options?: CallOptions) => Observable<R>
    : (input: T, options?: CallOptions) => Observable<R>;

type IsComposite<K extends RouteKey> = [HasBody<K>, HasQuery<K>, HasPath<K>] extends [false, false, false]
  ? false
  : [HasBody<K>, HasQuery<K>] extends [true, true]
    ? true
    : [HasBody<K>, HasPath<K>] extends [true, true]
      ? true
      : [HasQuery<K>, HasPath<K>] extends [true, true]
        ? true
        : false;

export type RouteFn<K extends RouteKey, R> =
  IsComposite<K> extends true
    ? (args: CompositeArgs<K>) => Promise<R>
    : HasBody<K> extends true
      ? SingleArgFn<BodyOf<K>, R>
      : HasQuery<K> extends true
        ? SingleArgFn<QueryOf<K>, R>
        : HasPath<K> extends true
          ? SingleArgFn<PathOf<K>, R>
          : (options?: CallOptions) => Promise<R>;

export type RouteFn$<K extends RouteKey, R> =
  IsComposite<K> extends true
    ? (args: CompositeArgs<K>) => Observable<R>
    : HasBody<K> extends true
      ? SingleArgFn$<BodyOf<K>, R>
      : HasQuery<K> extends true
        ? SingleArgFn$<QueryOf<K>, R>
        : HasPath<K> extends true
          ? SingleArgFn$<PathOf<K>, R>
          : (options?: CallOptions) => Observable<R>;

export type DefineRouteOptions = RequestExtras;

export type RequestByRouteArgs<K extends RouteKey> = {
  query?: QueryOf<K> extends never ? never : QueryOf<K>;
  body?: BodyOf<K> extends never ? never : BodyOf<K>;
  path?: PathOf<K> extends never ? never : PathOf<K>;
  signal?: AbortSignal;
  timeoutMs?: number;
  context?: ClipHttpContext;
};

type DispatchArgs = {
  query?: unknown;
  body?: unknown;
  path?: unknown;
  signal?: AbortSignal;
  timeoutMs?: number;
  context?: ClipHttpContext;
};

const appendFormValue = (formData: FormData, key: string, value: unknown) => {
  if (value === null || value === undefined) {
    return;
  }

  if (value instanceof File || value instanceof Blob) {
    formData.append(key, value);
    return;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      appendFormValue(formData, key, item);
    });
    return;
  }

  if (typeof value === "object") {
    formData.append(key, JSON.stringify(value));
    return;
  }

  formData.append(key, String(value));
};

const getValueByPath = (source: unknown, path: string): unknown => {
  if (!source || typeof source !== "object") {
    return undefined;
  }

  return path.split(".").reduce<unknown>((acc, key) => {
    if (!acc || typeof acc !== "object") {
      return undefined;
    }
    return (acc as Record<string, unknown>)[key];
  }, source);
};

const toFormData = (body: Record<string, unknown>, binaryFields: readonly string[]): FormData => {
  const formData = new FormData();
  const binaryTopLevelKeys = new Set(binaryFields.map((item) => item.split(".")[0]));

  binaryFields.forEach((binaryPath) => {
    const key = binaryPath.split(".").slice(-1)[0];
    const value = getValueByPath(body, binaryPath);
    appendFormValue(formData, key, value);
  });

  Object.entries(body).forEach(([key, value]) => {
    if (binaryTopLevelKeys.has(key)) {
      return;
    }
    appendFormValue(formData, key, value);
  });

  return formData;
};

const shouldUseFormData = (bodyKind: string) => bodyKind === "binary-json" || bodyKind === "multipart";

const buildUrl = (template: string, pathParams: readonly string[], pathArg: unknown): string => {
  if (pathParams.length === 0) {
    return template;
  }

  const pathRecord =
    pathArg && typeof pathArg === "object" && !Array.isArray(pathArg) ? (pathArg as Record<string, unknown>) : {};

  return pathParams.reduce(
    (url, key) => url.replace(`{${key}}`, encodeURIComponent(String(pathRecord[key] ?? ""))),
    template,
  );
};

const transformBodyForRequest = (body: unknown, meta: RouteMetaMap[RouteKey]) => {
  if (body && typeof body === "object" && !(body instanceof FormData) && shouldUseFormData(meta.bodyKind)) {
    return toFormData(body as Record<string, unknown>, meta.binaryFields);
  }
  return body;
};

const dispatchRequest = <K extends RouteKey>(route: K, args: DispatchArgs) => {
  const meta = routeMeta[route];
  return clipHttpClient.request<ResponseOf<K>>({
    method: meta.method as SupportedMethod,
    url: buildUrl(meta.path, meta.pathParams, args.path),
    query: args.query as QueryOf<K>,
    body: transformBodyForRequest(args.body, meta) as BodyOf<K>,
    signal: args.signal,
    timeoutMs: args.timeoutMs,
    context: args.context,
  });
};

const dispatchRequest$ = <K extends RouteKey>(route: K, args: DispatchArgs) => {
  const meta = routeMeta[route];
  return clipHttpClient.request$<ResponseOf<K>>({
    method: meta.method as SupportedMethod,
    url: buildUrl(meta.path, meta.pathParams, args.path),
    query: args.query as QueryOf<K>,
    body: transformBodyForRequest(args.body, meta) as BodyOf<K>,
    signal: args.signal,
    timeoutMs: args.timeoutMs,
    context: args.context,
  });
};

/**
 * 命令式底层 API：当 `defineRoute` 的智能签名无法满足复杂业务（条件式 query/body、运行时切换路由等）时使用。
 */
export const requestByRoute = <K extends RouteKey>(route: K, args: RequestByRouteArgs<K>) =>
  dispatchRequest(route, args);

export const requestByRoute$ = <K extends RouteKey>(route: K, args: RequestByRouteArgs<K>) =>
  dispatchRequest$(route, args);

const isPlainObject = (input: unknown): input is Record<string, unknown> =>
  input !== null &&
  typeof input === "object" &&
  !Array.isArray(input) &&
  !(input instanceof FormData) &&
  !(input instanceof Blob) &&
  !(input instanceof File);

const buildDispatchArgs = <K extends RouteKey>(
  meta: RouteMetaMap[K],
  options: DefineRouteOptions | undefined,
  input: unknown,
  callOptions?: CallOptions,
): DispatchArgs => {
  const hasBody = meta.bodyKind !== "none";
  const hasQuery = meta.hasQuery;
  const hasPath = meta.pathParams.length > 0;
  const flagsCount = (hasBody ? 1 : 0) + (hasQuery ? 1 : 0) + (hasPath ? 1 : 0);
  const isComposite = flagsCount > 1;

  let query: unknown;
  let body: unknown;
  let path: unknown;
  let timeoutMs = options?.timeoutMs;
  let context = options?.context;
  let signal: AbortSignal | undefined;

  if (isComposite && isPlainObject(input)) {
    query = input.query;
    body = input.body;
    path = input.path;
    if (input.timeoutMs !== undefined) {
      timeoutMs = input.timeoutMs as number;
    }
    if (input.context !== undefined) {
      context = input.context as ClipHttpContext;
    }
    if (input.signal !== undefined) {
      signal = input.signal as AbortSignal;
    }
  } else if (input !== undefined) {
    if (hasBody) {
      body = input;
    } else if (hasQuery) {
      query = input;
    } else if (hasPath) {
      path = input;
    }
  }

  if (callOptions?.signal !== undefined) {
    signal = callOptions.signal;
  }

  return { query, body, path, signal, timeoutMs, context };
};

export type DefinedRoute<K extends RouteKey, R> = RouteFn<K, R> & {
  /**
   * 覆盖返回类型，常用于 OpenAPI 推导出的 envelope 与业务期待的实际类型不一致时。
   * 由于 TypeScript 不支持「显式传前面的泛型 + 自动推导后面的泛型」，无法做成 `defineRoute<R>(route)`，
   * 因此采用链式 `defineRoute(route).as<R>()` 形态。
   */
  as: <NewR>() => DefinedRoute<K, NewR>;
};

const createDefinedRoute = <K extends RouteKey, R>(
  route: K,
  options: DefineRouteOptions | undefined,
): DefinedRoute<K, R> => {
  const meta = routeMeta[route];

  const fn = (input?: unknown, callOptions?: CallOptions) => {
    const dispatchArgs = buildDispatchArgs(meta, options, input, callOptions);
    return dispatchRequest(route, dispatchArgs);
  };

  (fn as unknown as { as: <NewR>() => DefinedRoute<K, NewR> }).as = <NewR>() =>
    createDefinedRoute<K, NewR>(route, options);

  return fn as DefinedRoute<K, R>;
};

const createDefinedRoute$ = <K extends RouteKey, R>(
  route: K,
  options: DefineRouteOptions | undefined,
): RouteFn$<K, R> => {
  const meta = routeMeta[route];
  const fn = (input?: unknown, callOptions?: CallOptions) => {
    const dispatchArgs = buildDispatchArgs(meta, options, input, callOptions);
    return dispatchRequest$(route, dispatchArgs);
  };
  return fn as RouteFn$<K, R>;
};

/**
 * 把一条路由声明成一个强类型函数。
 *
 * - 默认返回类型 = OpenAPI 推导的 `ResponseOf<K>`。
 * - 入参形态根据路由的 query/body/path 自动推导：仅一种存在时直传，多种共存时合并成 `{ query, body, path, timeoutMs, context }`。
 * - 需要覆盖返回类型时链式 `.as<R>()`。
 */
export function defineRoute<K extends RouteKey>(
  route: K,
  options?: DefineRouteOptions,
): DefinedRoute<K, ResponseOf<K>> {
  return createDefinedRoute<K, ResponseOf<K>>(route, options);
}

export function defineRoute$<K extends RouteKey>(route: K, options?: DefineRouteOptions): RouteFn$<K, ResponseOf<K>> {
  return createDefinedRoute$<K, ResponseOf<K>>(route, options);
}
